import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Edit,
  Copy,
  Trash2,
  Mail,
  Users,
  UserPlus,
  FileText,
  Calendar,
  Clock,
  Video,
  MapPin,
  Loader2,
} from 'lucide-react';
import { CourseFormModal } from './CourseFormModal';
import { coursesApi } from '../../../lib/api';
import { useAuth } from '../../../app/providers/AuthProvider';

interface Teacher {
  id: string;
  name: string;
  avatar?: string;
}

interface Meeting {
  id: string;
  index: number;
  date: string;
  weekday: string;
  startTime: string;
  endTime: string;
  teacher: Teacher;
  description: string;
  location: string;
  type: 'online' | 'onsite' | 'hybrid';
  attendancePercentage: number;
}

interface Course {
  id: string;
  name: string;
  cohort: string;
  code: string;
  grade: string;
  className: string;
  status: 'active' | 'completed' | 'upcoming';
  teachers: Teacher[];
  studentCount: number;
  meetingsCount: number;
  duration: string;
  price: number;
  progress: number;
  days: string[];
  time: string;
  meetings: Meeting[];
  fieldOfStudy: string;
  isOnlineOnly: boolean;
}

// Mock data
const mockCourses: Course[] = [
  {
    id: '1',
    name: 'Mathematics Advanced',
    cohort: 'Year 1 / Cohort A2',
    code: 'MATH-101',
    grade: 'Grade 10',
    className: 'Class A',
    status: 'active',
    teachers: [
      { id: '1', name: 'Sarah Cohen', avatar: '' },
    ],
    studentCount: 25,
    meetingsCount: 14,
    duration: '3 months',
    price: 1500,
    progress: 45,
    days: ['Sun', 'Tue', 'Thu'],
    time: '09:00 - 10:30',
    fieldOfStudy: 'Mathematics',
    isOnlineOnly: false,
    meetings: [
      {
        id: 'm1',
        index: 1,
        date: '2024-01-15',
        weekday: 'Monday',
        startTime: '09:00',
        endTime: '10:30',
        teacher: { id: '1', name: 'Sarah Cohen' },
        description: 'Introduction to Calculus',
        location: 'Room 101',
        type: 'onsite',
        attendancePercentage: 92,
      },
      {
        id: 'm2',
        index: 2,
        date: '2024-01-17',
        weekday: 'Wednesday',
        startTime: '09:00',
        endTime: '10:30',
        teacher: { id: '1', name: 'Sarah Cohen' },
        description: 'Derivatives',
        location: 'Online',
        type: 'online',
        attendancePercentage: 88,
      },
    ],
  },
  {
    id: '2',
    name: 'Physics Fundamentals',
    cohort: 'Year 1 / Cohort B1',
    code: 'PHY-101',
    grade: 'Grade 10',
    className: 'Class B',
    status: 'active',
    teachers: [
      { id: '2', name: 'David Levi', avatar: '' },
    ],
    studentCount: 22,
    meetingsCount: 12,
    duration: '3 months',
    price: 1400,
    progress: 60,
    days: ['Mon', 'Wed'],
    time: '11:00 - 12:30',
    fieldOfStudy: 'Physics',
    isOnlineOnly: false,
    meetings: [],
  },
  {
    id: '3',
    name: 'English Literature',
    cohort: 'Year 2 / Cohort A1',
    code: 'ENG-201',
    grade: 'Grade 11',
    className: 'Class A',
    status: 'active',
    teachers: [
      { id: '3', name: 'Rachel Green', avatar: '' },
    ],
    studentCount: 28,
    meetingsCount: 16,
    duration: '4 months',
    price: 1600,
    progress: 30,
    days: ['Sun', 'Tue', 'Thu'],
    time: '14:00 - 15:30',
    fieldOfStudy: 'English',
    isOnlineOnly: true,
    meetings: [],
  },
];

const grades = ['All Grades', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];

export function CoursesTab() {
  const { currentSchool } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('All Grades');
  const [expandedCourseId, setExpandedCourseId] = useState<string | null>(null);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [contextMenuCourseId, setContextMenuCourseId] = useState<string | null>(null);

  // Fetch courses from API
  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await coursesApi.list();
      // Transform API response to match our Course interface
      const apiCourses = (response.courses || []).map((c: any) => ({
        id: c.id || c.public_id,
        name: c.name,
        cohort: c.cohort || 'Default Cohort',
        code: c.code || `COURSE-${c.id}`,
        grade: c.grade || 'All Grades',
        className: c.class_name || 'Class A',
        status: c.status || 'active',
        teachers: c.teachers || [],
        studentCount: c.students_count || 0,
        meetingsCount: c.meetings_count || 0,
        duration: c.duration || '3 months',
        price: c.price || 0,
        progress: c.progress || 0,
        days: c.schedule_days || [],
        time: c.schedule_time || '09:00 - 10:30',
        fieldOfStudy: c.field_of_study || '',
        isOnlineOnly: c.is_online_only || false,
        meetings: c.meetings || [],
      }));
      setCourses(apiCourses.length > 0 ? apiCourses : mockCourses);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      // Fall back to mock data if API fails
      setCourses(mockCourses);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourse = async (courseData: any) => {
    if (!currentSchool) {
      alert('No school selected. Please select a school first.');
      return;
    }

    try {
      setSaving(true);

      // Transform form data to API format
      const apiData = {
        school_id: currentSchool.id,
        name: courseData.name,
        cohort: courseData.cohort || null,
        start_date: courseData.startDate || null,
        field_of_study: courseData.fieldOfStudy || null,
        delivery_mode: courseData.isOnlineOnly ? 'online' : 'hybrid',
        description: courseData.description || null,
      };

      if (editingCourse) {
        // Update existing course
        await coursesApi.update(editingCourse.id, apiData);
      } else {
        // Create new course
        await coursesApi.create(apiData);
      }
      // Refresh the list
      await fetchCourses();
      setShowCourseForm(false);
      setEditingCourse(null);
    } catch (error: any) {
      console.error('Failed to save course:', error);
      const message = error?.message || 'Failed to save course. Please try again.';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCourseApi = async (courseId: string) => {
    if (!confirm('Are you sure you want to delete this course?')) return;
    try {
      await coursesApi.delete(courseId);
      await fetchCourses();
    } catch (error) {
      console.error('Failed to delete course:', error);
      alert('Failed to delete course. Please try again.');
    }
    setContextMenuCourseId(null);
  };

  const filteredCourses = courses.filter((course) => {
    const matchesSearch = course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.code.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGrade = selectedGrade === 'All Grades' || course.grade === selectedGrade;
    return matchesSearch && matchesGrade;
  });

  const handleToggleExpand = (courseId: string) => {
    setExpandedCourseId(expandedCourseId === courseId ? null : courseId);
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setShowCourseForm(true);
    setContextMenuCourseId(null);
  };

  const handleCopyLink = (courseId: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/course/${courseId}`);
    setContextMenuCourseId(null);
  };

  const handleDeleteCourse = (courseId: string) => {
    handleDeleteCourseApi(courseId);
  };

  const getStatusBadge = (status: Course['status']) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      completed: 'bg-gray-100 text-gray-700',
      upcoming: 'bg-blue-100 text-blue-700',
    };
    const labels = {
      active: 'Active',
      completed: 'Completed',
      upcoming: 'Upcoming',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getDeliveryModeIcon = (type: Meeting['type']) => {
    switch (type) {
      case 'online':
        return <Video className="w-4 h-4 text-blue-500" />;
      case 'onsite':
        return <MapPin className="w-4 h-4 text-green-500" />;
      case 'hybrid':
        return (
          <div className="flex">
            <Video className="w-4 h-4 text-blue-500" />
            <MapPin className="w-4 h-4 text-green-500 -ml-1" />
          </div>
        );
    }
  };

  return (
    <div>
      {/* Filters and Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          {/* Grade Filter */}
          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            {grades.map((grade) => (
              <option key={grade} value={grade}>{grade}</option>
            ))}
          </select>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-64"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Users className="w-4 h-4" />
            All Students
          </button>
          <button
            onClick={() => {
              setEditingCourse(null);
              setShowCourseForm(true);
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Course
          </button>
        </div>
      </div>

      {/* Courses List */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-2" />
            <p className="text-gray-500">Loading courses...</p>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <p className="text-gray-500">No courses found. Create your first course!</p>
            <button
              onClick={() => setShowCourseForm(true)}
              className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700"
            >
              Create Course
            </button>
          </div>
        ) : null}
        {!loading && filteredCourses.map((course) => (
          <div
            key={course.id}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            {/* Course Header Row */}
            <div
              className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => handleToggleExpand(course.id)}
            >
              {/* Teacher Avatar */}
              <div className="flex -space-x-2">
                {course.teachers.slice(0, 3).map((teacher, idx) => (
                  <div
                    key={teacher.id}
                    className="w-10 h-10 rounded-full bg-teal-100 border-2 border-white flex items-center justify-center text-sm font-medium text-teal-700"
                    style={{ zIndex: 3 - idx }}
                  >
                    {teacher.name.split(' ').map(n => n[0]).join('')}
                  </div>
                ))}
              </div>

              {/* Course Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900 truncate">{course.name}</h3>
                  {getStatusBadge(course.status)}
                  {course.isOnlineOnly && (
                    <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                      Online Only
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">{course.cohort}</p>
              </div>

              {/* Course Stats */}
              <div className="hidden md:flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{course.studentCount} Students</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{course.meetingsCount} Meetings</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{course.days.join(', ')} {course.time}</span>
                </div>
              </div>

              {/* Progress */}
              <div className="hidden lg:flex items-center gap-2 w-32">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-teal-500 rounded-full"
                    style={{ width: `${course.progress}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600">{course.progress}%</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditCourse(course);
                  }}
                  className="px-3 py-1 text-sm text-teal-600 hover:bg-teal-50 rounded-lg"
                >
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyLink(course.id);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setContextMenuCourseId(contextMenuCourseId === course.id ? null : course.id);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>

                  {/* Context Menu */}
                  {contextMenuCourseId === course.id && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCourse(course);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Edit className="w-4 h-4" />
                        Edit Course
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextMenuCourseId(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Plus className="w-4 h-4" />
                        Add New Meeting
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextMenuCourseId(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Copy className="w-4 h-4" />
                        Duplicate Course
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextMenuCourseId(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Mail className="w-4 h-4" />
                        Send Mail to All
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextMenuCourseId(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <UserPlus className="w-4 h-4" />
                        Add Students
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextMenuCourseId(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <FileText className="w-4 h-4" />
                        Finance Report
                      </button>
                      <hr className="my-1" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCourse(course.id);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Course
                      </button>
                    </div>
                  )}
                </div>

                {/* Expand/Collapse Icon */}
                {expandedCourseId === course.id ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>

            {/* Expanded Meetings List */}
            {expandedCourseId === course.id && (
              <div className="border-t border-gray-200 bg-gray-50">
                <div className="p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Meetings Schedule</h4>
                  <div className="space-y-2">
                    {course.meetings.length > 0 ? (
                      course.meetings.map((meeting) => (
                        <div
                          key={meeting.id}
                          className="flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-200"
                        >
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-sm font-medium text-teal-700">
                            {meeting.index}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{meeting.description}</span>
                              {getDeliveryModeIcon(meeting.type)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {meeting.weekday}, {meeting.date} | {meeting.startTime} - {meeting.endTime}
                            </div>
                          </div>
                          <div className="text-sm text-gray-600">
                            {meeting.location}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-teal-500 rounded-full"
                                style={{ width: `${meeting.attendancePercentage}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-600">{meeting.attendancePercentage}%</span>
                          </div>
                          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">No meetings scheduled yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {filteredCourses.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No courses found</p>
          </div>
        )}
      </div>

      {/* Course Form Modal */}
      {showCourseForm && (
        <CourseFormModal
          course={editingCourse}
          onClose={() => {
            setShowCourseForm(false);
            setEditingCourse(null);
          }}
          onSave={handleSaveCourse}
        />
      )}
    </div>
  );
}
